import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';
import { ExperienceIcon } from '../../utils/IconWrapper';
import Section from '../../components/ui/Section';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.div`
  background-image: url('https://www.dailymood.it/wp-content/uploads/2018/04/home-35.jpg');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const ServiceTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--accent-primary);
  }
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  svg {
    margin-top: 5px;
    margin-right: 1rem;
    min-width: 24px;
    color: var(--accent-primary);
  }
`;

const FeatureText = styled.div`
  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
  }
  
  p {
    color: var(--text-secondary);
    line-height: 1.6;
  }
`;

const CallToAction = styled.div`
  background-color: var(--bg-secondary);
  padding: 3rem;
  text-align: center;
  border-radius: 8px;
  margin: 3rem 0;
`;

const CTATitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const CTAText = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const Experiences: React.FC = () => {
  const { language } = useLanguage();
  
  return (
    <PageContainer>
      <Helmet>
        <title>
          {language === 'it' 
            ? 'Servizi/Experience | J-O-H' 
            : 'Services/Experience | J-O-H'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Esperienze personalizzate per arricchire il tuo soggiorno: tour enogastronomici, corsi di cucina, escursioni private e molto altro.' 
            : 'Personalized experiences to enrich your stay: food and wine tours, cooking classes, private excursions, and much more.'} 
        />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {language === 'it' ? 'Servizi/Experience' : 'Services/Experience'}
          </HeroTitle>
          <HeroSubtitle>
            {language === 'it'
              ? 'Esperienze uniche e personalizzate per rendere il tuo soggiorno indimenticabile.'
              : 'Unique and personalized experiences to make your stay unforgettable.'}
          </HeroSubtitle>
          <Button variant="primary" to="/contact">
            {language === 'it' ? 'Richiedi Informazioni' : 'Request Information'}
          </Button>
        </HeroContent>
      </HeroSection>
      
      <Section padding="6rem 0">
        <Content>
          <ServiceTitle>
            {language === 'it' ? 'Esperienze Personalizzate' : 'Personalized Experiences'}
          </ServiceTitle>
          
          <ServiceDescription>
            {language === 'it'
              ? 'Arricchisci il tuo soggiorno con le nostre esperienze personalizzate. Dai tour enogastronomici ai corsi di cucina, dalle escursioni private alle lezioni di italiano, offriamo una vasta gamma di servizi esclusivi progettati per soddisfare i tuoi interessi e creare ricordi indimenticabili.'
              : 'Enrich your stay with our personalized experiences. From food and wine tours to cooking classes, from private excursions to Italian lessons, we offer a wide range of exclusive services designed to cater to your interests and create unforgettable memories.'}
          </ServiceDescription>
          
          <ServiceDescription>
            {language === 'it'
              ? 'Ogni esperienza è curata nei minimi dettagli e può essere personalizzata in base alle tue preferenze e interessi specifici, per garantirti momenti indimenticabili che vanno oltre il semplice soggiorno.'
              : 'Each experience is carefully curated and can be customized based on your specific preferences and interests, to guarantee unforgettable moments that go beyond a simple stay.'}
          </ServiceDescription>
          
          <FeatureList>
            <FeatureItem>
              <ExperienceIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Tour Enogastronomici' : 'Food and Wine Tours'}</h3>
                <p>
                  {language === 'it'
                    ? 'Scopri i sapori autentici della Toscana e della Liguria con tour personalizzati alle migliori cantine, frantoi e produttori locali, con degustazioni guidate dai nostri esperti.'
                    : 'Discover the authentic flavors of Tuscany and Liguria with personalized tours to the best wineries, oil mills, and local producers, with tastings guided by our experts.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <ExperienceIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Corsi di Cucina' : 'Cooking Classes'}</h3>
                <p>
                  {language === 'it'
                    ? 'Impara a preparare piatti tradizionali italiani con corsi di cucina tenuti da chef professionisti, utilizzando ingredienti freschi locali e tecniche autentiche.'
                    : 'Learn to prepare traditional Italian dishes with cooking classes held by professional chefs, using fresh local ingredients and authentic techniques.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <ExperienceIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Escursioni Private' : 'Private Excursions'}</h3>
                <p>
                  {language === 'it'
                    ? 'Esplora le bellezze naturali e storiche della regione con escursioni private, tour culturali e visite guidate alle città d\'arte, accompagnato da guide esperte multilingue.'
                    : 'Explore the natural and historical beauties of the region with private excursions, cultural tours, and guided visits to art cities, accompanied by expert multilingual guides.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <ExperienceIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Servizi di Concierge' : 'Concierge Services'}</h3>
                <p>
                  {language === 'it'
                    ? 'Dal trasporto privato alle prenotazioni nei migliori ristoranti, dallo shopping personalizzato ai servizi di benessere in villa, il nostro team è a disposizione per soddisfare ogni tua richiesta.'
                    : 'From private transportation to reservations at the best restaurants, from personalized shopping to wellness services in the villa, our team is at your disposal to satisfy your every request.'}
                </p>
              </FeatureText>
            </FeatureItem>
          </FeatureList>
          
          <CallToAction>
            <CTATitle>
              {language === 'it' ? 'Crea la Tua Esperienza Personalizzata' : 'Create Your Personalized Experience'}
            </CTATitle>
            <CTAText>
              {language === 'it'
                ? 'Contattaci oggi per discutere i tuoi interessi e lasciati ispirare dalle infinite possibilità che possiamo offrirti per arricchire il tuo soggiorno.'
                : 'Contact us today to discuss your interests and be inspired by the endless possibilities we can offer to enrich your stay.'}
            </CTAText>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci Ora' : 'Contact Us Now'}
            </Button>
          </CallToAction>
        </Content>
      </Section>
    </PageContainer>
  );
};

export default Experiences; 
